import { useEffect, useRef, useState } from 'react';
import { Modal, ModalDialog, Box, Typography, Avatar } from '@mui/joy';
import { getRtcConfig, prefetchRtcConfig } from '../../../utils/turnCredentials';
import ShowMyVideo from './ShowMyVideo';
import ButtonsIncoming from './ButtonsIncoming';
import ButtonsOutcoming from './ButtonsOutcoming';
import ShowPersonVideo from './ShowPersonVideo';
import BackgroundBlobs from './BackgroundBlobs';
import { mediaUrl } from '../../../utils/mediaUrl';

// Длительность разговора: "0:23", "12:45", а от часа — "01:23:45".
function formatDuration(totalSec) {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = String(totalSec % 60).padStart(2, '0');
    if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${s}`;
    return `${m}:${s}`;
}

const avatarSrc = (avatar) => mediaUrl(avatar) ?? undefined;

export default function CallInterface({
    socket,
    currentUserId,
    targetUser,
    isIncomingCall,
    incomingOfferRef,
    isReconnect,
    onClose
}) {
    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [callStatus, setCallStatus] = useState(
        isReconnect ? 'reconnecting' : isIncomingCall ? 'incoming' : 'calling'
    );
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(true);
    const [callSeconds, setCallSeconds] = useState(0);
    // Показывать кнопку переворота камеры только когда есть что переключать:
    // мобильное устройство или ≥2 видеокамеры.
    const [canSwitchCamera, setCanSwitchCamera] = useState(false);
    // Трансляция экрана (getDisplayMedia есть на десктоп-браузерах, нет на мобильных).
    const [canShareScreen, setCanShareScreen] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const screenTrackRef = useRef(null);
    // true — экран добавлен новым треком (камеры не было) и на стопе его надо удалить
    // через renegotiation; false — экран подменил уже существующий трек камеры.
    const screenAddedRef = useRef(false);
    // Видим ли реальное видео собеседника (а не только аудио) — иначе показываем аватар.
    const [remoteVideoOn, setRemoteVideoOn] = useState(false);

    const peerConnection = useRef(null);
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const ringAudioRef = useRef(null);
    const localStreamRef = useRef(null);
    // Звонок стартует аудио-only; видео-трек появляется при включении камеры.
    const videoTrackRef = useRef(null);
    const makingOfferRef = useRef(false);
    // Grace-таймер на транзиентные просадки соединения (в т.ч. при renegotiation).
    const dropTimerRef = useRef(null);

    const candidatesQueue = useRef([]);

    const startOutgoingCall = async (stream) => {
        socket.emit('call:start', {
            toUserId: targetUser.id,
            callerInfo: {
                firstName: targetUser.firstName ?? '',
                lastName: targetUser.lastName ?? '',
                avatar: targetUser.avatar ?? '',
            }
        });

        await createPeerConnection(stream);

        const offer = await peerConnection.current.createOffer();
        await peerConnection.current.setLocalDescription(offer);

        await new Promise((resolve) => {
            if (peerConnection.current.iceGatheringState === 'complete') {
                return resolve();
            }
            peerConnection.current.onicegatheringstatechange = () => {
                if (peerConnection.current.iceGatheringState === 'complete') resolve();
            };
            setTimeout(resolve, 3000);
        });

        const finalOffer = peerConnection.current.localDescription;
        // console.log('Sending offer, type:', finalOffer?.type, 'candidates in SDP:',
        //     (finalOffer?.sdp?.match(/a=candidate/g) || []).length);

        socket.emit('call:offer', { toUserId: targetUser.id, offer: finalOffer });
    };

    useEffect(() => {
        let mounted = true;

        const initMedia = async () => {
            try {
                // Креды TURN тянем параллельно с доступом к микрофону: их выдача
                // — обычный запрос к API, и ждать его отдельно перед соединением
                // значит задержать звонок на лишний round-trip.
                prefetchRtcConfig();

                // Аудио-only старт (симметрично мобильному клиенту): камеру не трогаем,
                // видео добавляется позже по запросу через addTrack + renegotiation.
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

                if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }

                setLocalStream(stream);
                localStreamRef.current = stream;

                if (localVideoRef.current) {
                    localVideoRef.current.srcObject = stream;
                    localVideoRef.current.play().catch(() => { });
                }

                if (!isIncomingCall) {
                    await startOutgoingCall(stream);
                }

            } catch (err) {
                console.error('Media error:', err);
                onClose();
            }
        };

        initMedia();
        return () => { mounted = false; cleanup(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isReconnect) return;

        // Ждём пока появится и стрим и оффер, затем авто-принимаем
        const interval = setInterval(() => {
            if (localStreamRef.current && incomingOfferRef.current) {
                clearInterval(interval);
                acceptCall();
            }
        }, 100);

        // По таймауту не молчим: показываем «Соединение потеряно» и закрываем.
        const timeout = setTimeout(() => {
            clearInterval(interval);
            setCallStatus('failed');
            setTimeout(onClose, 1500);
        }, 15000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReconnect]);

    useEffect(() => {
        if (!socket) return;

        const handleAnswer = async ({ answer }) => {
            if (!peerConnection.current) return;
            if (peerConnection.current.signalingState === 'stable') {
                // Повторный/поздний ответ (бывает при restart) — не обрабатываем, но логируем.
                console.warn('[call] answer received in stable state — ignored');
                return;
            }
            try {
                await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
                // ВАЖНО: ICE-кандидаты собеседника могли прийти ДО ответа и осесть в очереди.
                // Без их добавления соединение у инициатора не достраивается — нет медиа.
                await processCandidateQueue();
                setCallStatus('connected');
            } catch (e) {
                console.error("Error setting remote answer:", e);
            }
        };

        const handleIce = async ({ candidate }) => {
            const iceCandidate = new RTCIceCandidate(candidate);

            if (peerConnection.current && peerConnection.current.remoteDescription) {
                try {
                    await peerConnection.current.addIceCandidate(iceCandidate);
                } catch (e) {
                    console.error("Error adding ice candidate", e);
                }
            } else {
                // console.log("Buffering ICE candidate...");
                candidatesQueue.current.push(iceCandidate);
            }
        };

        const handleEnd = () => {
            onClose();
        };

        const handleRestart = async ({ toUserId }) => {
            // Закрываем старый PeerConnection
            if (peerConnection.current) {
                peerConnection.current.close();
                peerConnection.current = null;
            }

            // Создаём новый и отправляем оффер
            const stream = localStreamRef.current;
            if (!stream) return;

            await createPeerConnection(stream);

            const offer = await peerConnection.current.createOffer();
            await peerConnection.current.setLocalDescription(offer);

            await new Promise((resolve) => {
                if (peerConnection.current.iceGatheringState === 'complete') return resolve();
                peerConnection.current.onicegatheringstatechange = () => {
                    if (peerConnection.current.iceGatheringState === 'complete') resolve();
                };
                setTimeout(resolve, 3000);
            });

            socket.emit('call:offer', {
                toUserId,
                offer: peerConnection.current.localDescription
            });
        };

        // Пересогласование во время звонка (собеседник включил/выключил видео).
        const handleReoffer = async ({ offer }) => {
            const pc = peerConnection.current;
            if (!pc || pc.signalingState !== 'stable') return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(offer));
                await processCandidateQueue();
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                socket.emit('call:reanswer', { toUserId: targetUser.id, answer });
            } catch (e) {
                console.error('handleReoffer error:', e);
            }
        };

        const handleReanswer = async ({ answer }) => {
            const pc = peerConnection.current;
            if (!pc || pc.signalingState !== 'have-local-offer') return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(answer));
            } catch (e) {
                console.error('handleReanswer error:', e);
            }
        };

        socket.on('call:answer', handleAnswer);
        socket.on('call:ice', handleIce);
        socket.on('call:end', handleEnd);
        socket.on('call:restart', handleRestart);
        socket.on('call:reoffer', handleReoffer);
        socket.on('call:reanswer', handleReanswer);

        return () => {
            socket.off('call:answer', handleAnswer);
            socket.off('call:ice', handleIce);
            socket.off('call:end', handleEnd);
            socket.off('call:restart', handleRestart);
            socket.off('call:reoffer', handleReoffer);
            socket.off('call:reanswer', handleReanswer);
        };
    }, [socket, onClose]);

    useEffect(() => {
        const audio = ringAudioRef.current;
        if (!audio) return;

        if (callStatus === 'calling' || callStatus === 'incoming') {
            audio.play().catch(() => { });
        } else {
            audio.pause();
            audio.currentTime = 0;
        }
    }, [callStatus]);

    // Асинхронная: креды TURN живут сутки и приходят с сервера. Обычно они уже
    // лежат в кэше (прогрев при старте звонка), и await здесь ничего не стоит.
    const createPeerConnection = async (stream) => {
        if (peerConnection.current) return;

        const rtcConfig = await getRtcConfig();
        // Пока ждали ответ, звонок могли сбросить или соединение уже создали
        // другим путём — второй RTCPeerConnection затёр бы живой.
        if (peerConnection.current) return;

        peerConnection.current = new RTCPeerConnection(rtcConfig);

        peerConnection.current.onconnectionstatechange = () => {
            const state = peerConnection.current.connectionState;
            if (state === 'connected') {
                setCallStatus('connected');
                if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
            } else if (state === 'failed' || state === 'disconnected') {
                // Транзиентные просадки (особенно при renegotiation видео) не должны
                // мгновенно ронять звонок — даём время восстановиться.
                if (!dropTimerRef.current) {
                    dropTimerRef.current = setTimeout(() => {
                        setCallStatus('failed');
                        setTimeout(onClose, 1500);
                    }, 8000);
                }
            }
        };

        peerConnection.current.oniceconnectionstatechange = () => {
            // console.log('ICE state:', peerConnection.current.iceConnectionState);
        };

        stream.getTracks().forEach(track => {
            peerConnection.current.addTrack(track, stream);
        });

        peerConnection.current.ontrack = (event) => {
            const remote = event.streams[0];
            setRemoteStream(remote);
            if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = remote;
                remoteVideoRef.current.play().catch(() => { });
            }
            // Аватар или видео. НЕ реагируем на mute/unmute — это транзиентные сетевые
            // события (из-за них видео «само» мигало аватаркой). Реальный сигнал
            // «камеру выключили» — удаление трека (renegotiation → 'ended').
            const track = event.track;
            if (track?.kind === 'video') {
                setRemoteVideoOn(true);
                track.onended = () => setRemoteVideoOn(false);
            }
        };

        peerConnection.current.onicecandidate = (event) => {
            if (event.candidate) {
                socket.emit('call:ice', {
                    toUserId: targetUser.id,
                    candidate: event.candidate
                });
            }
        };
    };

    const processCandidateQueue = async () => {
        if (!peerConnection.current) return;

        // console.log(`Processing ${candidatesQueue.current.length} buffered candidates`);
        while (candidatesQueue.current.length > 0) {
            const candidate = candidatesQueue.current.shift();
            try {
                await peerConnection.current.addIceCandidate(candidate);
            } catch (e) {
                console.error("Error processing buffered candidate", e);
            }
        }
    };

    const acceptCall = async () => {
        const offer = await new Promise((resolve) => {
            if (incomingOfferRef.current) {
                return resolve(incomingOfferRef.current);
            }

            // console.log('Offer ещё не пришёл, ждём...');
            const interval = setInterval(() => {
                if (incomingOfferRef.current) {
                    clearInterval(interval);
                    resolve(incomingOfferRef.current);
                }
            }, 100);
            setTimeout(() => {
                clearInterval(interval);
                resolve(null);
            }, 10000);
        });

        if (!offer) {
            console.error('Offer так и не пришёл за 10 секунд');
            return;
        }

        // localStreamRef проставляется синхронно, стейт localStream может отставать на рендер —
        // источником истины берём ref.
        const stream = localStreamRef.current || await new Promise((resolve) => {
            const interval = setInterval(() => {
                if (localStreamRef.current) { clearInterval(interval); resolve(localStreamRef.current); }
            }, 100);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 5000);
        });

        if (!stream) { console.error('Не удалось получить медиапоток'); return; }

        await createPeerConnection(stream);

        try {
            await peerConnection.current.setRemoteDescription(
                new RTCSessionDescription(offer)
            );
            await processCandidateQueue();

            const answer = await peerConnection.current.createAnswer();
            await peerConnection.current.setLocalDescription(answer);

            socket.emit('call:answer', { toUserId: targetUser.id, answer });
            setCallStatus('connected');
        } catch (e) {
            console.error('acceptCall error:', e);
        }
    };

    const endCall = () => {
        if (socket && targetUser) {
            socket.emit('call:end', { toUserId: targetUser.id });
        }
        onClose();
    };

    const cleanup = () => {
        if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
        if (screenTrackRef.current) {
            screenTrackRef.current.stop();
            screenTrackRef.current = null;
        }
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
        }
        if (peerConnection.current) {
            peerConnection.current.close();
            peerConnection.current = null;
        }
        // Снимаем ссылку на остановленный стрим — иначе на части браузеров
        // индикатор камеры/микрофона остаётся «активным» после звонка.
        if (localVideoRef.current) localVideoRef.current.srcObject = null;
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    };

    const toggleMute = () => {
        if (localStream) {
            const audioTrack = localStream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);
            }
        }
    };

    // Пересогласование во время звонка: новый offer (видео добавили/убрали).
    // ICE летит отдельно (trickle), ждать сбора не нужно.
    const renegotiate = async () => {
        const pc = peerConnection.current;
        if (!pc || pc.signalingState !== 'stable') return;
        try {
            makingOfferRef.current = true;
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('call:reoffer', { toUserId: targetUser.id, offer: pc.localDescription });
        } catch (e) {
            console.error('renegotiate error:', e);
        } finally {
            makingOfferRef.current = false;
        }
    };

    const enableVideo = async () => {
        if (videoTrackRef.current || !peerConnection.current || !localStreamRef.current) return;
        try {
            const vstream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
            const vtrack = vstream.getVideoTracks()[0];
            if (!vtrack) return;
            videoTrackRef.current = vtrack;
            localStreamRef.current.addTrack(vtrack);
            peerConnection.current.addTrack(vtrack, localStreamRef.current);
            setIsVideoOff(false);
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = localStreamRef.current;
                localVideoRef.current.play().catch(() => { });
            }
            await renegotiate();
        } catch (e) {
            console.error('enableVideo error:', e);
            videoTrackRef.current = null;
        }
    };

    const disableVideo = async () => {
        const vtrack = videoTrackRef.current;
        if (!vtrack) return;
        const sender = peerConnection.current?.getSenders().find(s => s.track === vtrack);
        if (sender && peerConnection.current) {
            try { peerConnection.current.removeTrack(sender); } catch (e) { console.error(e); }
        }
        try { localStreamRef.current?.removeTrack(vtrack); } catch (e) { console.error(e); }
        try { vtrack.stop(); } catch (e) { console.error(e); }
        videoTrackRef.current = null;
        setIsVideoOff(true);
        await renegotiate();
    };

    const toggleVideo = () => {
        if (isVideoOff) enableVideo();
        else disableVideo();
    };

    // Переключение камеры (фронтальная/тыловая или следующая по списку). На вебе
    // нет _switchCamera как в RN — берём следующее videoinput-устройство, получаем
    // новый трек и подменяем его в peer connection через replaceTrack (без переустановки SDP).
    const switchCamera = async () => {
        const stream = localStreamRef.current;
        const current = videoTrackRef.current;
        if (!stream || !current) return; // переключать нечего, камера выключена
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cams = devices.filter(d => d.kind === 'videoinput');
            if (cams.length < 2) return;

            const currentId = current.getSettings?.().deviceId;
            const idx = cams.findIndex(c => c.deviceId === currentId);
            const next = cams[(idx + 1) % cams.length];

            const newStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: next.deviceId } },
                audio: false,
            });
            const newTrack = newStream.getVideoTracks()[0];

            const sender = peerConnection.current?.getSenders().find(s => s.track && s.track.kind === 'video');
            if (sender) await sender.replaceTrack(newTrack); // без renegotiation

            stream.removeTrack(current); current.stop();
            stream.addTrack(newTrack);
            videoTrackRef.current = newTrack;

            if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        } catch (e) {
            console.error('switchCamera error:', e);
        }
    };

    // ─── Трансляция экрана ────────────────────────────────────────────────────
    // Подменяем видео-дорожку в peer connection на дорожку экрана через replaceTrack
    // (без переустановки SDP). Собеседник видит экран в том же окне удалённого видео.
    const stopScreenShare = async () => {
        const screenTrack = screenTrackRef.current;
        if (screenTrack) { screenTrack.stop(); screenTrackRef.current = null; }
        const sender = peerConnection.current?.getSenders().find(s => s.track === screenTrack)
            || peerConnection.current?.getSenders().find(s => s.track && s.track.kind === 'video');

        if (screenAddedRef.current) {
            // Камеры не было — убираем видео-дорожку и пересогласовываем.
            if (sender && peerConnection.current) {
                try { peerConnection.current.removeTrack(sender); } catch (e) { console.error(e); }
            }
            screenAddedRef.current = false;
            await renegotiate();
        } else {
            // Возвращаем камеру вместо экрана (renegotiation не нужен — m-line есть).
            const camTrack = videoTrackRef.current;
            if (sender && camTrack) {
                try { await sender.replaceTrack(camTrack); } catch (e) { console.error('stopScreenShare error:', e); }
            }
        }
        setIsScreenSharing(false);
    };

    const startScreenShare = async () => {
        try {
            const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
            const screenTrack = display.getVideoTracks()[0];
            if (!screenTrack) return;

            const sender = peerConnection.current?.getSenders().find(s => s.track && s.track.kind === 'video');
            if (sender) {
                // Камера уже включена — просто подменяем дорожку (без renegotiation).
                await sender.replaceTrack(screenTrack);
                screenAddedRef.current = false;
            } else {
                // Аудио-звонок без камеры — добавляем дорожку экрана и пересогласовываем.
                peerConnection.current?.addTrack(screenTrack, localStreamRef.current);
                screenAddedRef.current = true;
                await renegotiate();
            }

            screenTrackRef.current = screenTrack;
            setIsScreenSharing(true);

            // Пользователь остановил трансляцию через системную плашку браузера.
            screenTrack.onended = () => { stopScreenShare(); };
        } catch (e) {
            // Отмена выбора окна/экрана — это не ошибка.
            if (e?.name !== 'NotAllowedError') console.error('startScreenShare error:', e);
        }
    };

    const toggleScreenShare = () => {
        if (isScreenSharing) stopScreenShare();
        else startScreenShare();
    };

    // ─── Доступность переворота камеры ───────────────────────────────────────
    // enumerateDevices отдаёт полный список (с deviceId) только после выдачи
    // доступа к камере, поэтому считаем устройства уже после инициализации стрима.
    useEffect(() => {
        if (!localStream) return;
        let cancelled = false;
        const ua = navigator.userAgent || '';
        const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
            // iPadOS прикидывается Mac — ловим по тач-поинтам.
            || (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.platform));
        // Шаринг экрана: getDisplayMedia есть на десктопе, на мобильных браузерах — нет.
        setCanShareScreen(!isMobile && typeof navigator.mediaDevices?.getDisplayMedia === 'function');
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                if (cancelled) return;
                const cams = devices.filter(d => d.kind === 'videoinput').length;
                setCanSwitchCamera(cams >= 2 || isMobile);
            })
            .catch(() => { if (!cancelled) setCanSwitchCamera(isMobile); });
        return () => { cancelled = true; };
    }, [localStream]);

    // ─── Таймер длительности разговора ───────────────────────────────────────
    useEffect(() => {
        if (callStatus !== 'connected') return;
        const start = Date.now();
        setCallSeconds(0);
        const id = setInterval(() => setCallSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
        return () => clearInterval(id);
    }, [callStatus]);

    const displayName = [targetUser?.firstName, targetUser?.lastName].filter(Boolean).join(' ') || 'Пользователь';
    const initials = [targetUser?.firstName?.[0], targetUser?.lastName?.[0]].filter(Boolean).join('').toUpperCase();
    const statusText =
        callStatus === 'connected' ? formatDuration(callSeconds)
            : callStatus === 'incoming' ? 'Входящий звонок'
                : callStatus === 'reconnecting' ? 'Восстановление соединения…'
                    : callStatus === 'failed' ? 'Соединение потеряно'
                        : 'Вызов…';

    return (
        <>
            <audio ref={ringAudioRef} src="/sounds/ringtone.mp3" loop />
            <Modal open={true} onClose={() => { }}>
                <ModalDialog
                    variant="outlined"
                    layout="center"
                    sx={{
                        width: '100vw',
                        height: '100dvh',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        p: 0,
                        overflow: 'hidden',
                        borderRadius: '0px !important',
                        border: 'none',
                        bgcolor: '#001B3D',
                    }}
                >
                    <BackgroundBlobs />

                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <ShowPersonVideo
                            remoteVideoRef={remoteVideoRef}
                            remoteStream={remoteVideoOn ? remoteStream : null}
                        />

                        {/* Имя звонящего и статус/длительность — всегда сверху по центру */}
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0,
                            pt: 6, px: 3, zIndex: 3,
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            pointerEvents: 'none',
                            textShadow: remoteVideoOn ? '0 1px 8px rgba(0,0,0,0.6)' : 'none',
                        }}>
                            <Typography level="h2" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }} noWrap>
                                {displayName}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5, fontSize: 15 }}>
                                {statusText}
                            </Typography>
                        </Box>

                        {/* Аватар по центру, пока нет живого видео собеседника */}
                        {!remoteVideoOn && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                                <Avatar
                                    src={avatarSrc(targetUser?.avatar)}
                                    sx={{
                                        '--Avatar-size': '128px',
                                        fontSize: 44,
                                        fontWeight: 600,
                                        bgcolor: 'rgba(255,255,255,0.16)',
                                        color: '#fff',
                                        border: '1px solid rgba(255,255,255,0.18)',
                                    }}
                                >
                                    {initials}
                                </Avatar>
                            </Box>
                        )}

                        <ShowMyVideo
                            localVideoRef={localVideoRef}
                            showVideo={!isVideoOff}
                        />

                        <Box sx={{
                            position: 'absolute',
                            bottom: 40,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 3,
                            zIndex: 3
                        }}>
                            {callStatus === 'incoming' ? (
                                <ButtonsIncoming
                                    endCall={endCall}
                                    acceptCall={acceptCall}
                                />
                            ) : (
                                <ButtonsOutcoming
                                    isMuted={isMuted}
                                    toggleMute={toggleMute}
                                    endCall={endCall}
                                    isVideoOff={isVideoOff}
                                    toggleVideo={toggleVideo}
                                    switchCamera={switchCamera}
                                    canSwitchCamera={canSwitchCamera}
                                    toggleScreenShare={toggleScreenShare}
                                    isScreenSharing={isScreenSharing}
                                    canShareScreen={canShareScreen}
                                />
                            )}
                        </Box>
                    </Box>
                </ModalDialog>
            </Modal>
        </>
    );
}
